/**
 * STAFF QUERY KEYS.
 *
 * The list call takes no company parameter: the token scopes it to one tenant.
 * The key carries `tenantId` anyway, so one operator's rows are not served as
 * the next operator's. The profile key adds the staff id. The list key is a
 * prefix of the profile key, so invalidating the list invalidates that
 * tenant's profiles too.
 */
export const staffKeys = {
  list: (tenantId: string) => ["settings", "staff", tenantId] as const,
  profile: (tenantId: string, staffId: string) => ["settings", "staff", tenantId, staffId] as const,
};

/**
 * The company and the branch list take no company parameter: the token scopes
 * them. The key carries `tenantId` anyway, so one operator's rows are not
 * served as the next operator's. A branch's roles add the branch code.
 */
export const companyKeys = {
  profile: (tenantId: string) => ["settings", "company", tenantId] as const,
};

export const branchKeys = {
  list: (tenantId: string) => ["settings", "branches", tenantId] as const,
  roles: (tenantId: string, branchCode: string) =>
    ["settings", "branches", tenantId, branchCode, "roles"] as const,
};

/**
 * One logo file per branch. Kept off `branchKeys.list`, so saving an address
 * does not refetch the file, and the list payload carries no bytes. The tenant
 * is on the key so one operator's file is not served as the next operator's.
 */
export const branchLogoKeys = {
  logo: (tenantId: string, branchCode: string) => ["settings", "branch-logo", tenantId, branchCode] as const,
};

/** The catalogue is system-wide. The key still carries the tenant, for the same reason. */
export const permissionKeys = {
  catalogue: (tenantId: string) => ["settings", "permissions", tenantId] as const,
};

/**
 * The access log is scoped by the token. The key carries the tenant anyway,
 * and every parameter that changes the page: the page, its size, the two
 * dates, the sort and the staff id. A blank sort and an omitted sort are the
 * same key, and a blank staff id and an omitted one are the same key — the
 * staff id is trimmed first, matching what goes on the wire.
 */
export const accessLogKeys = {
  list: (
    tenantId: string,
    query: {
      pageNumber: number;
      pageSize: number;
      startDate: string;
      endDate: string;
      sort?: string;
      staffId?: string;
    },
  ) =>
    [
      "settings",
      "access-log",
      tenantId,
      query.pageNumber,
      query.pageSize,
      query.startDate,
      query.endDate,
      query.sort ?? "",
      query.staffId?.trim() ?? "",
    ] as const,
};
